import React, {useEffect, useState} from "react";
import {KeyTextString} from "./types";
import {Autocomplete, AutocompleteRenderOptionState, Chip, CircularProgress, MenuItem, TextField} from "@mui/material";
import {FormGenContentItem, FormGenLayoutSource} from "./FormGenContainer";
import {BORDER_RADIUS, SZ_XSM} from "./dimens";
import {validateEmail} from "./text_util";
import {colorPastelRed} from "./colors";

export type FormGenFreetextType = "text" | "email";

class FormGenAutocompleteOption {
  constructor(readonly type: "keytext" | "freetext", readonly item: KeyTextString) {
  }
}

function valuesToOptions(newValues: any | any[], options: FormGenAutocompleteOption[], freetextType?: FormGenFreetextType): FormGenAutocompleteOption[] {
  if (Array.isArray(newValues)) {
    return newValues.filter(Boolean).map(value => valueToOption(value, options, freetextType));
  }
  return [valueToOption(newValues, options, freetextType)];
}

function valueToOption(value: FormGenAutocompleteOption | FormGenAutocompleteOption[] | string | string[], options: FormGenAutocompleteOption[], freetextType?: FormGenFreetextType): FormGenAutocompleteOption {
  if (value instanceof FormGenAutocompleteOption) {
    return value as FormGenAutocompleteOption;
  }
  if (freetextType) {
    const str = value as string;
    return new FormGenAutocompleteOption("freetext", new KeyTextString(freetextType + ":" + str, str));
  }
  return options.find(option => option.item.key === value);
}

function optionText(option: FormGenAutocompleteOption): string {
  let text = option?.item?.text;
  const description = option?.item?.description;
  if (description) {
    text += " " + description;
  }
  return text;
}

export function FormGenAutocomplete(props: {
  contentItem: FormGenContentItem,
  disabled?: boolean,
  index: number,
  source: FormGenLayoutSource,
  showDescriptionInInput?: boolean,
  showDescriptionInMenu?: boolean,
}) {
  const [loading, setLoading] = useState<boolean>(true);
  const [currentOption, setCurrentOption] = useState<FormGenAutocompleteOption | FormGenAutocompleteOption[]>();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<FormGenAutocompleteOption[]>([]);
  const [reloadOptions, setReloadOptions] = useState(false);
  const [inputText, setInputText] = useState("");

  useEffect(() => {
    (async () => {
      const value = props.source.getValue(props.contentItem);
      const options: FormGenAutocompleteOption[] = (await props.contentItem.metadata.autocompleteLoader(value))?.map(keytext => new FormGenAutocompleteOption("keytext", keytext));
      setOptions(options);
      setLoading(false);
      if (options?.length > 0) {
        if (multiple) {
          const keys = value as string[];
          setCurrentOption(options.filter(option => keys?.includes(option.item.key)));
        } else {
          const key = value as string;
          const currentOption = options.find(option => option.item.key === key);
          setCurrentOption(currentOption);
        }
      }
    })();
  }, []);

  useEffect(() => {
    let active = true;
    if (!reloadOptions) {
      return;
    }
    setReloadOptions(false);
    setLoading(true);
    (async () => {
      const value = props.source.getValue(props.contentItem);
      const options: FormGenAutocompleteOption[] = [];
      options.push(...(await props.contentItem.metadata.autocompleteLoader(value, inputText))?.map(keytext => new FormGenAutocompleteOption("keytext", keytext)));
      // if (currentOption) {
      //   options.push(currentOption);
      // }
      if (active) {
        setOptions(options);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [reloadOptions]);

  const multiple = props.contentItem.metadata.autocompleteMulti;
  return <Autocomplete
    disabled={props.disabled}
    key={Array.isArray(currentOption) ? currentOption.map(option => option.item.key).join("") : currentOption?.item.key}
    disablePortal
    autoSelect
    multiple={multiple}
    filterSelectedOptions
    freeSolo={Boolean(props.contentItem.metadata.autocompleteFreetextType)}
    renderTags={(values: any, getTagProps) =>
      values.filter(Boolean).map((value, index) => {
        let backgroundColor = null;
        const option = valueToOption(value, options, props.contentItem.metadata.autocompleteFreetextType);
        if (option.type === "freetext" && props.contentItem.metadata.autocompleteFreetextType === "email" && !validateEmail(option.item.text)) {
          backgroundColor = colorPastelRed;
        }
        return <Chip style={{backgroundColor: backgroundColor, borderRadius: BORDER_RADIUS, height: 20}}
                     label={option.item.text} {...getTagProps({index})} />
      })
    }
    open={open}
    onOpen={() => {
      setOpen(true);
    }}
    onClose={() => {
      setOpen(false);
    }}
    value={currentOption}
    onChange={(event: any, newValue) => {
      const valueOptions: FormGenAutocompleteOption[] = valuesToOptions(newValue, options, props.contentItem.metadata.autocompleteFreetextType);
      const value = multiple ? valueOptions.map(option => option.item.key) : valueOptions[0]?.item.key;
      props.source.setValue(props.contentItem, value);
      setCurrentOption(multiple ? valuesToOptions(value, options) : valueToOption(value, options));
    }}
    inputValue={inputText}
    onInputChange={(event, newInputValue) => {
      setInputText(newInputValue);
      if (props.contentItem.metadata.autocompleteResetOnChange) {
        setReloadOptions(true);
      }
    }}
    // getOptionSelected={(option, value) => option.key === value.key}
    //@ts-ignore
    getOptionLabel={(option: FormGenAutocompleteOption) =>
      props.showDescriptionInInput
        ? optionText(option)
        : option?.item?.text}
    options={options}
    loading={loading}
    renderOption={
      props.showDescriptionInMenu
        // If showDescriptionInMenu is true, then we render the keyText description in the dropdown.
        ? (props, option: FormGenAutocompleteOption, state: AutocompleteRenderOptionState) =>
          <MenuItem {...props} {...state}>{optionText(option)}</MenuItem>
        : undefined}
    renderInput={(params) => (
      <TextField
        {...params}
        //label={props.contentItem.metadata.name}
        variant="outlined"
        size="small"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={SZ_XSM}/> : null}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
      />
    )}
  />;
}
